var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { action, computed, makeObservable, observable, when } from 'mobx';
import { AssetErrorMessage, AssetStatusType, } from './types';
import { generateImagePreview, getImageInfoByClassification, shouldGenerateImagePreview, } from '../../artworkProcessing/imageClassification';
import { fetchAssetIdFromSherbertUrl, VariantTypes } from '../../artworkProcessing/utility';
import { createAssetByFileWithPolling, createAssetByUriWithPolling, deleteAsset, getVariantMetadataWithPolling, hideAsset, patchAsset, presignAsset, unhideAsset, getImageInfoWithPolling, getPageImageInfo, } from '../../client';
import { DEFAULT_SHERBERT_BASE_URL, UPLOADS_BASE_URL } from '../../client/baseUrl';
import { isAbortError, naiveRandomId } from '../../utilities';
import { isAssetFromData, isDerivedAsset, isFileAsset, isUriAsset } from '../typeGuards';
import { assetStoreEvent, AssetStoreEvent } from '../assetStoreEvent';
import { getQueryParams } from '../../artworkProcessing/common';
const POLLING_INTERVAL_MS = 50;
const MAX_POLLING_COUNT = 300;
export class Asset {
    constructor(options) {
        var _a, _b, _c;
        /**
         * An  identifier which can be used to distinguish between instances of the `Asset` class. Helpful when {@link Asset.data} is not yet available.
         *
         * @example
         * ```jsx
         * <ul>
         *  {assets.map(asset =>
         *      <li key={asset.instanceId}>...</li>
         *  )}
         * </ul>
         * ```
         */
        Object.defineProperty(this, "instanceId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: naiveRandomId()
        });
        Object.defineProperty(this, "composeOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "authProvider", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "uploadOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "preparePromise", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "variantCache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_pageImageInfo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "assetStoreEventTarget", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "status", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { type: AssetStatusType.Unprepared }
        });
        Object.defineProperty(this, "_data", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_signature", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        makeObservable(this);
        this.authProvider = options.authProvider;
        this.variantCache = new Map();
        this.assetStoreEventTarget = options.assetStoreEventTarget;
        if (isDerivedAsset(options)) {
            this.composeOptions = Object.assign({}, options);
        }
        else if (isAssetFromData(options)) {
            const { assetData, signature } = options;
            this.setAssetData(assetData);
            if ((_a = assetData.info.asset) === null || _a === void 0 ? void 0 : _a.hidden) {
                this.setStatus({ type: AssetStatusType.Deleted, isHidden: true });
            }
            else {
                this.setStatus({ type: AssetStatusType.Prepared });
            }
            this.setSignature(signature);
        }
        else {
            this.uploadOptions = options;
            if ((_c = (_b = options.info) === null || _b === void 0 ? void 0 : _b.asset) === null || _c === void 0 ? void 0 : _c.hidden) {
                this.setStatus({ type: AssetStatusType.Deleted, isHidden: true });
            }
        }
        when(() => this.status.type === AssetStatusType.Prepared, () => __awaiter(this, void 0, void 0, function* () {
            var _d, _e, _f, _g;
            if (!this.isPrepared() || this.data.type !== 'image')
                return;
            if (((_d = this.data.info.asset) === null || _d === void 0 ? void 0 : _d.sourceUri) && this.data.info.image)
                return;
            try {
                if (this.data.isLocallyProcessed || ((_e = this.data.info.asset) === null || _e === void 0 ? void 0 : _e.sourceUri)) {
                    const { data: imageInfo } = yield getImageInfoWithPolling({ path: { id: this.data.id } }, { headers: yield this.getAuthHeaders() });
                    const updatedAssetData = Object.assign(Object.assign({}, this.data), { info: Object.assign(Object.assign({}, this.data.info), { image: imageInfo }), isLocallyProcessed: false });
                    this.setAssetData(updatedAssetData);
                    const assetImageInfo = {
                        id: this.data.id,
                        imageInfo: imageInfo,
                    };
                    (_f = this.assetStoreEventTarget) === null || _f === void 0 ? void 0 : _f.dispatchEvent(assetStoreEvent(AssetStoreEvent.ImageInfoServerSuccess, { detail: assetImageInfo }));
                }
                // Don't set image info if the request fails, but don't throw an error either
                // eslint-disable-next-line no-empty
            }
            catch (_h) {
                const assetImageInfo = {
                    id: this.data.id,
                    imageInfo: undefined,
                };
                (_g = this.assetStoreEventTarget) === null || _g === void 0 ? void 0 : _g.dispatchEvent(assetStoreEvent(AssetStoreEvent.ImageInfoServerFailure, { detail: assetImageInfo }));
            }
        }));
    }
    get data() {
        return this._data;
    }
    // Derived variants
    get original() {
        return this.derive('original');
    }
    get print() {
        return this.derive('print');
    }
    get webPreview() {
        return this.derive('webPreview');
    }
    get thumbnail() {
        return this.derive('thumb');
    }
    get embroidery() {
        return this.derive('embroidery');
    }
    get monochrome() {
        return this.derive('monochrome');
    }
    get sharpened() {
        return this.derive('sharpened');
    }
    get backgroundRemoved() {
        return this.derive('backgroundRemoved');
    }
    /**
     * cerate variant and return metadata of the asset.
     */
    getMetadata(assetData, variant) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { data: variantAssetData } = yield getVariantMetadataWithPolling({ path: { id: assetData.id, variant } }, { headers: yield this.getAuthHeaders() });
                return variantAssetData;
            }
            catch (error) {
                throw new Error('unable to create variant');
            }
        });
    }
    /**
     * Return print variant of the asset.
     */
    getPrint() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.Print);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(`unable to create variant: ${VariantTypes.Print}`);
            }
        });
    }
    /**
     * Return webPreview variant of the asset.
     */
    getWebPreview() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.WebPreview);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(`unable to create variant: ${VariantTypes.WebPreview}`);
            }
        });
    }
    /**
     * Return Thumb variant of the asset.
     */
    getThumb() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.Thumb);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(`unable to create variant: ${VariantTypes.Thumb}`);
            }
        });
    }
    /**
     * Return Sharpened variant of the asset.
     */
    getSharpened() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.Sharpened);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(`unable to create variant: ${VariantTypes.Sharpened}`);
            }
        });
    }
    /**
     * Return background removed variant of the asset.
     */
    getBackgroundRemoved() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.BackgroundRemoved);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(`unable to create variant: ${VariantTypes.BackgroundRemoved}`);
            }
        });
    }
    /**
     * Return monochrome removed variant of the asset.
     */
    getMonochrome() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.Monochrome);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(`unable to create variant: ${VariantTypes.Monochrome}`);
            }
        });
    }
    /**
     * Return background removed variant of the asset.
     */
    getEmbroidery() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.Embroidery);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(`unable to create variant: ${VariantTypes.Embroidery}`);
            }
        });
    }
    /**
     * Gets the URL for the asset of the appropriate AssetType value, for example Print, Preview, or Original variants.
     * @param {GetUrlParams} ParametersObject used to pass parameters into this function. See type documentation for more detailed information.
     * This indicates whether or not to include the signature from the original so that presign doesn't
     * need to be called separately.
     * @returns {string} Returns the requested asset URL.
     */
    getUrl({ includeSignature = true, showDeleted = false, pageNum } = {}) {
        var _a, _b;
        switch ((_a = this.composeOptions) === null || _a === void 0 ? void 0 : _a.processTypeUrlPath) {
            case VariantTypes.Original:
                return this.getVariantPath(VariantTypes.Original, includeSignature, showDeleted, pageNum);
            case VariantTypes.Thumb:
                return this.getVariantPath(VariantTypes.Thumb, includeSignature, showDeleted, pageNum);
            case VariantTypes.Print:
                return this.getVariantPath(VariantTypes.Print, includeSignature, showDeleted, pageNum);
            case VariantTypes.WebPreview:
                return this.getVariantPath(VariantTypes.WebPreview, includeSignature, showDeleted, pageNum);
            case VariantTypes.Embroidery:
                return this.getVariantPath(VariantTypes.Embroidery, includeSignature, showDeleted, pageNum);
            case VariantTypes.BackgroundRemoved:
                return this.getVariantPath(VariantTypes.BackgroundRemoved, includeSignature, showDeleted, pageNum);
            case VariantTypes.Sharpened:
                return this.getVariantPath(VariantTypes.Sharpened, includeSignature, showDeleted, pageNum);
            case VariantTypes.Monochrome:
                return this.getVariantPath(VariantTypes.Monochrome, includeSignature, showDeleted, pageNum);
            default:
                throw Error(`Unknown asset type --> ${(_b = this.composeOptions) === null || _b === void 0 ? void 0 : _b.processTypeUrlPath}`);
        }
    }
    getVariantPath(variantType, includeSignature, showDeleted, pageNum) {
        var _a, _b, _c;
        const url = new URL(this.getUrlInternal());
        if (includeSignature) {
            url.searchParams.set('signature', this.signature ? this.signature : '');
        }
        if (pageNum !== null &&
            pageNum !== undefined &&
            variantType !== VariantTypes.WebPreview &&
            variantType !== VariantTypes.Thumb) {
            throw new Error(`MultiPage variant generation is not applicable for: ${variantType}`);
        }
        let pathName = url.pathname;
        if (variantType !== 'original') {
            pathName += `/${variantType}`;
        }
        if (((_c = (_b = (_a = this.composeOptions) === null || _a === void 0 ? void 0 : _a.parentAsset._data) === null || _b === void 0 ? void 0 : _b.info.image) === null || _c === void 0 ? void 0 : _c.format) === 'pdf') {
            if (variantType !== 'original' &&
                pageNum !== undefined &&
                this.composeOptions.parentAsset._data.info.image.numPages) {
                if (pageNum >= 1 && pageNum <= this.composeOptions.parentAsset._data.info.image.numPages) {
                    pathName += `/${pageNum}`;
                }
                else {
                    throw new Error(`page number must be in range: [1-${this.composeOptions.parentAsset._data.info.image.numPages}]`);
                }
            }
        }
        url.pathname = pathName;
        const pathUrl = `${url.toString()}&showDeleted=${showDeleted}`;
        return pathUrl;
    }
    getUrlInternal() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
        let url = DEFAULT_SHERBERT_BASE_URL;
        if ((_b = (_a = this.composeOptions) === null || _a === void 0 ? void 0 : _a.parentAsset._data) === null || _b === void 0 ? void 0 : _b.properties.url) {
            url = (_d = (_c = this.composeOptions) === null || _c === void 0 ? void 0 : _c.parentAsset._data) === null || _d === void 0 ? void 0 : _d.properties.url;
        }
        else if (((_h = (_g = (_f = (_e = this.composeOptions) === null || _e === void 0 ? void 0 : _e.parentAsset._data) === null || _f === void 0 ? void 0 : _f._links) === null || _g === void 0 ? void 0 : _g.self) === null || _h === void 0 ? void 0 : _h.href) !== undefined &&
            ((_m = (_l = (_k = (_j = this.composeOptions) === null || _j === void 0 ? void 0 : _j.parentAsset._data) === null || _k === void 0 ? void 0 : _k._links) === null || _l === void 0 ? void 0 : _l.self) === null || _m === void 0 ? void 0 : _m.href) !== null) {
            if ((_r = (_q = (_p = (_o = this.composeOptions) === null || _o === void 0 ? void 0 : _o.parentAsset._data) === null || _p === void 0 ? void 0 : _p._links) === null || _q === void 0 ? void 0 : _q.self) === null || _r === void 0 ? void 0 : _r.href.toLowerCase().startsWith(`${DEFAULT_SHERBERT_BASE_URL}/v2/assets/`)) {
                const assetId = fetchAssetIdFromSherbertUrl((_v = (_u = (_t = (_s = this.composeOptions) === null || _s === void 0 ? void 0 : _s.parentAsset._data) === null || _t === void 0 ? void 0 : _t._links) === null || _u === void 0 ? void 0 : _u.self) === null || _v === void 0 ? void 0 : _v.href);
                return `${DEFAULT_SHERBERT_BASE_URL}/v2/assets/${assetId}`;
            }
            url = (_z = (_y = (_x = (_w = this.composeOptions) === null || _w === void 0 ? void 0 : _w.parentAsset._data) === null || _x === void 0 ? void 0 : _x._links) === null || _y === void 0 ? void 0 : _y.self) === null || _z === void 0 ? void 0 : _z.href;
        }
        return url;
    }
    /**
     * Returns an unsigned URL of the asset or undefined if the asset is not prepared yet.
     * Using this unsigned URL will require additional authorization.
     */
    get unsignedUrl() {
        if (this.isVariant()) {
            const { parentAsset, processTypeUrlPath } = this.composeOptions;
            const parentUrl = parentAsset.unsignedUrl;
            return parentUrl ? `${parentUrl}/${processTypeUrlPath}` : undefined;
        }
        if (!this.hasAssetId()) {
            return undefined;
        }
        return `${DEFAULT_SHERBERT_BASE_URL}/v2/assets/${this.data.id}`;
    }
    /**
     * Returns a URL of the asset or undefined if the asset is not signed yet.
     */
    get signedUrl() {
        if (!this.isSigned()) {
            return undefined;
        }
        const url = new URL(this.unsignedUrl);
        url.searchParams.set('signature', this.signature);
        return url.toString();
    }
    /**
     * Creates an `Asset` instance with a `File` blob as the data source.
     */
    static fromFile(options) {
        return new Asset(options);
    }
    /**
     * Creates an `Asset` instance with a resource URL as the data source.
     */
    static fromUri(options) {
        return new Asset(options);
    }
    /**
     * Creates an `Asset` instance with Sherbert asset metadata as the data source.
     */
    static fromData(options) {
        return new Asset(options);
    }
    /**
     * Prepares the asset for use by creating or retrieving asset data.
     *
     * You can think of this operation as a synchronization between the client and server.
     * For example, calling `Asset.fromFile({ ... })` will create an `Asset` instance,
     * but calling `prepare` on that instance is what will actually make calls to the Sherbert API.
     *
     * If you're using `AssetStore.upload()`, this method will be automatically called for you.
     *
     * When `prepare` is called on an asset variant,
     * the variant will be automatically generated in Sherbert if it doesn't exist.
     *
     * The `prepare` operation is cached internally - any subsequent call to this method
     * will result in the initial Promise being returned.
     *
     * An optional `signal` parameter is supported to allow cancelling the operation on the client side.
     *
     * ------------------------------------------------------------------------------------------------
     *
     * @throws `Error` with message `Preparation of a variant asset is not supported yet` if the asset is a variant.
     * @throws `Error` with message `AssetErrorMessage.InvalidSourceData` if the asset is not of type `AssetFromFileOptions` or `AssetFromUriOptions`.
     * @throws `Error` with message `AssetErrorMessage.PrepareDeletingAsset` if deleting is in progress.
     * @throws `Error` with message `AssetErrorMessage.PrepareDeletedAsset` if the asset is not a base assets.
     */
    prepare(params = {}, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.status.type === AssetStatusType.Deleting) {
                throw new Error(AssetErrorMessage.PrepareDeletingAsset);
            }
            // It's possible to re-generate a deleted variant on-demand or prepare a hidden base asset,
            // so only throw for deleted base assets
            if (this.status.type === AssetStatusType.Deleted && !this.status.isHidden && !this.isVariant()) {
                throw new Error(AssetErrorMessage.PrepareDeletedAsset);
            }
            if (!this.preparePromise ||
                this.status.type === AssetStatusType.Unprepared ||
                this.status.type === AssetStatusType.Error) {
                this.preparePromise = this._prepare(params, callback);
            }
            return this.preparePromise;
        });
    }
    setPlaceHolderAsset(assetData) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.setStatus({ type: AssetStatusType.Prepared });
            if (assetData._data) {
                this.setAssetData(Object.assign(Object.assign({}, assetData._data), { info: Object.assign({}, (_a = assetData._data) === null || _a === void 0 ? void 0 : _a.info), isLocallyProcessed: true }));
            }
        });
    }
    /**
     * Sets the signature of the base asset. In case the base asset isn't prepared yet, it will also prepare it.
     *
     * ------------------------------------------------------------------------------------------------
     *
     * @throws `Error` with message `AssetErrorMessage.PresignFailed` if the asset could not be signed.
     * @throws `Error` with message `AssetErrorMessage.EmptySignature` if the signature is not included in the response.
     *
     * @param params
     */
    presign({ signal } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.signature ||
                this.status.type === AssetStatusType.Deleted ||
                this.status.type === AssetStatusType.Deleting) {
                return;
            }
            if (this.isVariant()) {
                yield this.baseAsset.presign();
                return;
            }
            // if no id, prepare first
            if (!this.isPrepared()) {
                yield this.prepare();
            }
            // In case prepare didn't set the asset data id, throw error
            if (!this.isPrepared()) {
                throw new Error(AssetErrorMessage.MissingAssetId);
            }
            let result;
            try {
                const headers = yield this.getAuthHeaders();
                // By default, the signature returned here never expires.
                result = (yield presignAsset({ path: { id: this.data.id } }, { signal, headers }));
            }
            catch (error) {
                if (isAbortError(error)) {
                    return;
                }
                throw new Error(AssetErrorMessage.PresignFailed);
            }
            if ((result === null || result === void 0 ? void 0 : result.status) === 200) {
                const { signature } = result.data;
                this.setSignature(signature);
                if (!signature) {
                    throw new Error(AssetErrorMessage.EmptySignature);
                }
            }
        });
    }
    /**
     * Deletes the current asset from the asset store by hiding it in sherbert by default.
     *
     * ------------------------------------------------------------------------------------------------
     *
     * @throws `Error` with message `AssetErrorMessage.MissingAssetId` if the asset ID is missing.
     *
     * @param params
     * @param params.hideOnly - Decides whether to hide or delete an asset. If set to `false`, the asset will be deleted from Sherbert.
     * Be aware that deleting an asset might break documents where this asset is being used. Defaults to `true`.
     */
    delete({ hideOnly = true } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.status.type === AssetStatusType.Deleting) {
                yield when(() => this.status.type !== AssetStatusType.Deleting);
                return;
            }
            // if the asset is preparing we can't be sure if the asset was created or not on the server,
            // so we have to wait for the asset (and its id) to be created before proceeding to delete it
            if (this.status.type === AssetStatusType.Preparing) {
                this.setStatus({ type: AssetStatusType.Deleting, isHidden: hideOnly });
                yield when(() => { var _a; return ((_a = this.data) === null || _a === void 0 ? void 0 : _a.id) !== undefined; });
            }
            if (
            // Cannot check for `isPrepared` here, because `prepare` won't set the status to `Prepared` when status is `Deleting`.
            this.hasAssetId() &&
                (this.status.type !== AssetStatusType.Deleted || this.isHidden()) &&
                this.status.type !== AssetStatusType.Unprepared) {
                try {
                    this.setStatus({ type: AssetStatusType.Deleting, isHidden: hideOnly });
                    const headers = yield this.getAuthHeaders();
                    if (hideOnly) {
                        yield hideAsset({ path: { id: this.data.id } }, { headers });
                    }
                    else {
                        yield deleteAsset({ path: { id: this.data.id } }, { headers });
                    }
                    // Allow re-generation of variants
                    if (this.isVariant()) {
                        this.preparePromise = undefined;
                    }
                }
                catch (error) {
                    this.setErrorStatus(error);
                    return;
                }
            }
            this.setStatus({ type: AssetStatusType.Deleted, isHidden: hideOnly });
        });
    }
    /**
     * Patches the data of the current asset and sets the `isPatching` status metadata accordingly.
     *
     * ------------------------------------------------------------------------------------------------
     *
     * @throws `Error` with message `AssetErrorMessage.PatchUnpreparedAsset` if the asset is not prepared.
     * @throws `Error` with message `AssetErrorMessage.PatchFailed` if an error occurred while patching.
     *
     * @param params
     */
    patchData(patchData) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isPrepared()) {
                throw new Error(AssetErrorMessage.PatchUnpreparedAsset);
            }
            try {
                this.setStatus({ type: AssetStatusType.Prepared, isPatching: true });
                const { data: updatedData } = yield patchAsset({ path: { id: this.data.id }, body: patchData }, { headers: yield this.getAuthHeaders() });
                this.setAssetData(updatedData);
                this.setStatus({ type: AssetStatusType.Prepared, isPatching: false });
            }
            catch (_a) {
                throw new Error(AssetErrorMessage.PatchFailed);
            }
        });
    }
    /**
     * Checks if both the base asset's signature and data id are set.
     *
     * @returns a type predicate that guarantees that `signedUrl`, `unsignedUrl` and `signature` are set.
     */
    isSigned() {
        var _a;
        return !!((_a = this.baseAsset.data) === null || _a === void 0 ? void 0 : _a.id) && !!this.signature;
    }
    /**
     * Checks if the asset has an id and if the status is `Prepared`.
     *
     * @returns a type predicate that guarantees that `unsignedUrl` and `data.id` are set.
     */
    isPrepared() {
        return this.status.type === AssetStatusType.Prepared && this.hasAssetId();
    }
    /** The top-most non-variant asset */
    get baseAsset() {
        if (this.isVariant()) {
            return this.composeOptions.parentAsset.baseAsset;
        }
        return this;
    }
    get signature() {
        // Using _signature property here to avoid an infinite loop
        // eslint-disable-next-line no-underscore-dangle
        return this.baseAsset._signature;
    }
    /**
     * DO NOT CALL THIS METHOD DIRECTLY! Use `this.prepare` instead.
     */
    _prepare({ signal, signedUrl } = {}, callback) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.status.type === AssetStatusType.Prepared) {
                return;
            }
            const uploadStartTime = new Date().getTime();
            try {
                const isHidden = this.isHidden();
                const isVariant = this.isVariant();
                // Hidden base assets should stay hidden
                if (!isHidden || isVariant) {
                    this.setStatus({ type: AssetStatusType.Preparing });
                }
                const headers = yield this.getAuthHeaders();
                if (isHidden && isVariant && ((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                    yield unhideAsset({ path: { id: this.data.id } }, { headers });
                }
                else if (this.isVariant()) {
                    yield this.baseAsset.prepare({ signal });
                    // TODO(#139): add support for nested variants
                    const { data } = yield getVariantMetadataWithPolling({
                        path: { id: this.baseAsset.data.id, variant: this.composeOptions.processTypeUrlPath },
                    }, { signal, headers });
                    this.setAssetData(data);
                }
                else if (isFileAsset(this.uploadOptions)) {
                    const _e = this.uploadOptions, { onProgress: handleOnProgress, authProvider } = _e, requestOptions = __rest(_e, ["onProgress", "authProvider"]);
                    let onProgress;
                    if (isHidden && !isVariant) {
                        onProgress = handleOnProgress;
                    }
                    else {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        onProgress = (percentComplete) => {
                            this.setStatus({ type: AssetStatusType.Preparing, uploadProgress: percentComplete });
                            handleOnProgress === null || handleOnProgress === void 0 ? void 0 : handleOnProgress(percentComplete);
                        };
                    }
                    let imageInfo;
                    let imageInfoData;
                    let pollingCount = MAX_POLLING_COUNT;
                    let imagePreview;
                    let uploadCallbackResponse;
                    try {
                        imageInfo = getImageInfoByClassification(this.uploadOptions.file);
                        if (callback instanceof Function) {
                            if (shouldGenerateImagePreview(this.uploadOptions.file)) {
                                imagePreview = generateImagePreview(this.uploadOptions.file, headers);
                                imagePreview.then((response) => {
                                    if (response && response[0] && response[0].uploadId) {
                                        imageInfo.then((info) => {
                                            var _a;
                                            imageInfoData = info;
                                            uploadCallbackResponse = {
                                                imageInfo: info,
                                                tempUploadData: response[0],
                                                tempUploadUrl: `${UPLOADS_BASE_URL}/${(_a = response[0]) === null || _a === void 0 ? void 0 : _a.uploadId}`,
                                                isError: false,
                                            };
                                            callback(uploadCallbackResponse);
                                        });
                                    }
                                });
                            }
                        }
                    }
                    catch (exception) {
                        uploadCallbackResponse = {
                            isError: true,
                        };
                        (_b = this.assetStoreEventTarget) === null || _b === void 0 ? void 0 : _b.dispatchEvent(assetStoreEvent(AssetStoreEvent.ImageInfoClientFailure, { detail: undefined }));
                        if (callback) {
                            callback(uploadCallbackResponse);
                        }
                    }
                    let assetData;
                    // upload asset using signed url
                    if (signedUrl) {
                        const queryParams = signedUrl.substring(signedUrl.indexOf('?') + 1);
                        try {
                            assetData = (yield createAssetByFileWithPolling({ body: Object.assign({}, requestOptions) }, { signal, onProgress, headers: undefined }, queryParams)).data;
                        }
                        catch (error) {
                            (_c = this.assetStoreEventTarget) === null || _c === void 0 ? void 0 : _c.dispatchEvent(assetStoreEvent(AssetStoreEvent.UploadUsingSignedUrlFailure, { detail: error.data }));
                            if (callback) {
                                callback(error.data);
                            }
                        }
                    }
                    else {
                        assetData = (yield createAssetByFileWithPolling({ body: Object.assign({}, requestOptions) }, { signal, onProgress, headers })).data;
                    }
                    if (assetData) {
                        const interval = setInterval(() => {
                            if (pollingCount <= 0 || imageInfoData) {
                                clearInterval(interval);
                            }
                            if (!imageInfoData) {
                                imageInfo === null || imageInfo === void 0 ? void 0 : imageInfo.then((info) => {
                                    var _a;
                                    const assetImageInfo = {
                                        id: assetData === null || assetData === void 0 ? void 0 : assetData.id,
                                        imageInfo: info,
                                    };
                                    (_a = this.assetStoreEventTarget) === null || _a === void 0 ? void 0 : _a.dispatchEvent(assetStoreEvent(AssetStoreEvent.ImageInfoClientSuccess, {
                                        detail: assetImageInfo,
                                    }));
                                    imageInfoData = info;
                                });
                            }
                            pollingCount -= 1;
                        }, POLLING_INTERVAL_MS);
                        const uploadEndTime = new Date().getTime();
                        const totalUploadTimeMs = uploadEndTime - uploadStartTime;
                        this.setAssetData(Object.assign(Object.assign({}, assetData), { info: Object.assign(Object.assign({}, assetData.info), { image: imageInfoData }), isLocallyProcessed: true, totalUploadTimeMs }));
                    }
                }
                else if (isUriAsset(this.uploadOptions)) {
                    const _f = this.uploadOptions, { authProvider } = _f, requestOptions = __rest(_f, ["authProvider"]);
                    let asset;
                    if (signedUrl) {
                        const queryParams = getQueryParams(signedUrl);
                        if (queryParams)
                            try {
                                asset = yield createAssetByUriWithPolling({
                                    body: Object.assign({}, requestOptions),
                                    queryParams: { signature: queryParams.signature, folderId: queryParams.folderId },
                                }, { signal });
                            }
                            catch (error) {
                                (_d = this.assetStoreEventTarget) === null || _d === void 0 ? void 0 : _d.dispatchEvent(assetStoreEvent(AssetStoreEvent.UploadUsingSignedUrlFailure, { detail: error.data }));
                                if (callback) {
                                    callback(error.data);
                                }
                            }
                    }
                    else {
                        asset = yield createAssetByUriWithPolling({ body: Object.assign({}, requestOptions) }, { signal, headers });
                    }
                    const uploadUriEndTime = new Date().getTime();
                    const totalUriUploadTimeMs = uploadUriEndTime - uploadStartTime;
                    this.setAssetData(Object.assign(Object.assign({}, asset.data), { totalUploadTimeMs: totalUriUploadTimeMs }));
                }
                else {
                    throw new Error(AssetErrorMessage.InvalidSourceData);
                }
                // Do not set Prepared status if the asset is being deleted during prepare or if the base asset
                // was hidden before being prepared (asset should always stay hidden in that case)
                if (this.status.type !== AssetStatusType.Deleting && (!isHidden || isVariant)) {
                    this.setStatus({ type: AssetStatusType.Prepared });
                }
            }
            catch (error) {
                if (this.status.type === AssetStatusType.Deleted) {
                    return;
                }
                if (isAbortError(error)) {
                    this.setStatus({ type: AssetStatusType.Unprepared });
                    return;
                }
                this.setErrorStatus(error);
            }
        });
    }
    /**
     * Get the Page info of the specified page
     * @param {GetImageInfoPerPageParams} pageNo denotes the page no of which metadata is required
     * @returns {Promise<ImageMetadata>} Returns metadata of the page specified
     */
    getPageImageInfo({ pageNo }) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            const imageMetadata = this._pageImageInfo[pageNo];
            if (imageMetadata !== undefined && imageMetadata !== null && Object.keys(imageMetadata).length !== 0) {
                return imageMetadata;
            }
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            if (pageNo < 1) {
                throw new Error(`${pageNo} is not a valid page number`);
            }
            if (((_b = this.data.info.image) === null || _b === void 0 ? void 0 : _b.format) === 'pdf' &&
                ((_c = this.data.info.image) === null || _c === void 0 ? void 0 : _c.numPages) &&
                ((_d = this.data.info.image) === null || _d === void 0 ? void 0 : _d.numPages) < pageNo) {
                throw new Error(`page number must in range: [1-${(_e = this.data.info.image) === null || _e === void 0 ? void 0 : _e.numPages}]`);
            }
            const imageInfo = (yield getPageImageInfo({ path: { id: (_f = this.data) === null || _f === void 0 ? void 0 : _f.id, pageNum: pageNo } }, { headers: yield this.getAuthHeaders() }));
            this._pageImageInfo[pageNo] = imageInfo.data;
            return imageInfo.data;
        });
    }
    /**
     * Returns cached or new `VariantAsset` associated with provided `processTypeUrlPath`.
     * `processTypeUrlPath` is also used in `unsignedUrl` and `signedUrl` for variant asset URL construction.
     */
    derive(processTypeUrlPath) {
        let variantAsset = this.variantCache.get(processTypeUrlPath);
        if (!variantAsset) {
            variantAsset = new Asset({
                parentAsset: this,
                processTypeUrlPath,
                authProvider: this.authProvider,
            });
            this.variantCache.set(processTypeUrlPath, variantAsset);
        }
        return variantAsset;
    }
    setAssetData(assetData) {
        this._data = assetData;
    }
    setStatus(status) {
        this.status = status;
        if ((status.type === AssetStatusType.Deleting || status.type === AssetStatusType.Deleted) && !status.isHidden) {
            this.variantCache.forEach((variant) => {
                variant.setStatus(status);
            });
        }
    }
    setErrorStatus(error) {
        if (error instanceof Error) {
            this.setStatus({ type: AssetStatusType.Error, rawError: error });
        }
        else if (typeof error === 'string') {
            this.setStatus({ type: AssetStatusType.Error, rawError: new Error(error) });
        }
        else {
            this.setStatus({ type: AssetStatusType.Error, rawError: new Error('Unknown error') });
        }
    }
    setSignature(signature) {
        this._signature = signature;
    }
    isVariant() {
        return !!this.composeOptions;
    }
    isHidden() {
        return this.status.type === AssetStatusType.Deleted && this.status.isHidden;
    }
    hasAssetId() {
        var _a;
        return !!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id);
    }
    getAuthHeaders() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Headers(yield this.authProvider.getAuthHeaders());
        });
    }
}
__decorate([
    observable
], Asset.prototype, "status", void 0);
__decorate([
    observable
], Asset.prototype, "_data", void 0);
__decorate([
    observable
], Asset.prototype, "_signature", void 0);
__decorate([
    computed
], Asset.prototype, "data", null);
__decorate([
    computed
], Asset.prototype, "unsignedUrl", null);
__decorate([
    computed
], Asset.prototype, "signedUrl", null);
__decorate([
    computed
], Asset.prototype, "baseAsset", null);
__decorate([
    computed
], Asset.prototype, "signature", null);
__decorate([
    action.bound
], Asset.prototype, "setAssetData", null);
__decorate([
    action.bound
], Asset.prototype, "setStatus", null);
__decorate([
    action.bound
], Asset.prototype, "setErrorStatus", null);
__decorate([
    action.bound
], Asset.prototype, "setSignature", null);
//# sourceMappingURL=Asset.js.map